import React, { Suspense, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";

import App from "./pages/App";
import MasterDashboard from "./pages/MasterDashboard";
import TeacherProfile from "./pages/TeacherProfile";
import TeacherClassroomTransitionModal, {
  isClassroomTransitionDevelopmentMock
} from "./components/TeacherClassroomTransitionModal";
import { exchangeClassroomTransitionHandoff } from "./services/TeacherAPIClient";
import {
  getTeacherAuthErrorMessage,
  validateTeacherToken
} from "./utils/auth";
import {
  SENSAI_EMBED_MODE,
  SENSAI_EMBED_SOURCE,
  TD_PORTAL_EMBED_MODE,
  getWorkflowLaunchMetadata,
  postSensaiWorkflowMessage,
  resolveAllowedParentOrigin
} from "./utils/sensaiEmbedWorkflow";

import "./fonts/Adia_Demo.ttf";
import "./index.scss";
import "bootstrap/dist/css/bootstrap.min.css";

const loadingFallback = (
  <div className="spinner-container">
    <div className="big-ball-spinner"></div>
  </div>
);

const CLASSROOM_TRANSITION_HANDOFF_ERROR =
  "This sign-in link is invalid or has expired. Return to Teacher Dashboard and try again.";

export function isLocalClassroomTransitionPreview({
  search = typeof window === "undefined" ? "" : window.location.search,
  hostname = typeof window === "undefined" ? "" : window.location.hostname,
  nodeEnv = process.env.NODE_ENV
} = {}) {
  return ["localhost", "127.0.0.1"].includes(hostname) &&
    isClassroomTransitionDevelopmentMock(search, nodeEnv);
}

function DevelopmentEntry() {
  const location = useLocation();

  if (!isLocalClassroomTransitionPreview({ search: location.search })) {
    return <App />;
  }

  return (
    <TeacherClassroomTransitionModal
      show
      handleClose={() => {}}
      schoolid="development-school"
      teacherid="development-teacher"
      currentTeacherName="Development Teacher"
      currentTeacherEmail="teacher@example.test"
      launchContext={{
        schoolId: "development-school",
        source: "development-preview"
      }}
    />
  );
}

function EmbeddedAuthError({ launchContext, message }) {
  const workflow = launchContext.workflow || "authentication";
  const launchId = launchContext.launchId || "";
  const targetOrigin = resolveAllowedParentOrigin(
    launchContext.parentOrigin,
    typeof document !== "undefined" ? document.referrer : ""
  );

  useEffect(() => {
    postSensaiWorkflowMessage({
      parentWindow: window.parent,
      targetOrigin,
      type: "workflow.error",
      workflow,
      launchId,
      status: "error",
      message
    });
  }, [launchId, message, targetOrigin, workflow]);

  return (
    <main className="d-flex min-vh-100 align-items-center justify-content-center p-4">
      <div className="alert alert-danger mb-0" role="alert">
        <h1 className="h5">Unable to open this teacher workflow</h1>
        <p className="mb-0">{message}</p>
      </div>
    </main>
  );
}

export function TeacherProtectedRoute({ element }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locationKey = `${location.pathname}${location.search}`;
  const currentSearchParams = new URLSearchParams(location.search);
  const hasIncomingHandoff = currentSearchParams.has("handoff");
  const requiresFreshAuthCheck =
    hasIncomingHandoff ||
    currentSearchParams.has("token") ||
    Boolean(location.state?.authError);
  const handoffRequestRef = useRef(null);
  const requestIdRef = useRef(0);
  const [authState, setAuthState] = useState({
    locationKey: "",
    status: "checking",
    reason: "missing",
    message: ""
  });

  useEffect(() => () => {
    requestIdRef.current += 1;
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const handoffCode = searchParams.get("handoff");
    const incomingToken = searchParams.get("token");
    const launchContext = getWorkflowLaunchMetadata(searchParams);
    const isClassroomTransitionLaunch =
      location.pathname === "/classroom-transition" ||
      launchContext.workflow === "classroom-transition";
    const isTeacherDashboardHandoff =
      launchContext.source === SENSAI_EMBED_SOURCE &&
      (launchContext.embed === SENSAI_EMBED_MODE || launchContext.embed === TD_PORTAL_EMBED_MODE) &&
      launchContext.workflow !== "classroom-transition" &&
      searchParams.has("token");
    const handoffError = location.state?.authError || "";

    if (searchParams.has("handoff")) {
      searchParams.delete("handoff");
      searchParams.delete("token");
      const cleanSearch = searchParams.toString();
      const cleanLocationKey = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}`;
      const requestId = ++requestIdRef.current;

      localStorage.removeItem("token");
      handoffRequestRef.current = { locationKey: cleanLocationKey, requestId };
      setAuthState({
        locationKey: cleanLocationKey,
        status: "checking",
        reason: "missing",
        message: ""
      });
      navigate(
        {
          pathname: location.pathname,
          search: cleanSearch ? `?${cleanSearch}` : ""
        },
        { replace: true, state: null }
      );

      const exchangeRequest = isClassroomTransitionLaunch
        ? exchangeClassroomTransitionHandoff(handoffCode)
        : Promise.reject(new Error("Invalid classroom transition hand-off route."));

      exchangeRequest
        .then((session) => {
          if (requestIdRef.current !== requestId) {
            return;
          }

          const validation = validateTeacherToken(session?.token);
          if (!validation.valid) {
            throw new Error("Invalid teacher session returned by hand-off exchange.");
          }

          localStorage.setItem("token", session.token);
          handoffRequestRef.current = null;
          setAuthState({
            locationKey: cleanLocationKey,
            status: "authenticated",
            reason: "",
            message: ""
          });
        })
        .catch(() => {
          if (requestIdRef.current !== requestId) {
            return;
          }

          localStorage.removeItem("token");
          handoffRequestRef.current = null;
          setAuthState({
            locationKey: cleanLocationKey,
            status: "rejected",
            reason: "invalid",
            message: CLASSROOM_TRANSITION_HANDOFF_ERROR
          });
        });

      return;
    }

    if (
      handoffRequestRef.current &&
      handoffRequestRef.current.locationKey === locationKey
    ) {
      return;
    }

    if (handoffRequestRef.current) {
      requestIdRef.current += 1;
      handoffRequestRef.current = null;
    }

    if (handoffError) {
      setAuthState({
        locationKey,
        status: "rejected",
        reason: "invalid",
        message: handoffError
      });
      return;
    }

    if (searchParams.has("token") && !isTeacherDashboardHandoff) {
      localStorage.removeItem("token");
      searchParams.delete("token");
      const cleanSearch = searchParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: cleanSearch ? `?${cleanSearch}` : ""
        },
        {
          replace: true,
          state: { authError: getTeacherAuthErrorMessage("invalid") }
        }
      );
      return;
    }

    const token = isTeacherDashboardHandoff
      ? incomingToken
      : localStorage.getItem("token");
    const validation = validateTeacherToken(token);
    const message = getTeacherAuthErrorMessage(validation.reason);

    if (isTeacherDashboardHandoff) {
      if (validation.valid) {
        localStorage.setItem("token", incomingToken);
      } else {
        localStorage.removeItem("token");
      }

      searchParams.delete("token");
      const cleanSearch = searchParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: cleanSearch ? `?${cleanSearch}` : ""
        },
        {
          replace: true,
          state: validation.valid ? null : { authError: message }
        }
      );

      return;
    }

    if (!validation.valid) {
      localStorage.removeItem("token");
    }

    setAuthState({
      locationKey,
      status: validation.valid ? "authenticated" : "rejected",
      reason: validation.reason,
      message
    });
  }, [location.pathname, location.search, location.state, locationKey, navigate]);

  if (
    authState.status === "checking" ||
    (requiresFreshAuthCheck && authState.locationKey !== locationKey)
  ) {
    return loadingFallback;
  }

  if (authState.status !== "authenticated") {
    const searchParams = new URLSearchParams(location.search);
    const launchContext = getWorkflowLaunchMetadata(searchParams);
    const isEmbedded =
      launchContext.embed === SENSAI_EMBED_MODE || launchContext.embed === TD_PORTAL_EMBED_MODE;

    if (isEmbedded) {
      return (
        <EmbeddedAuthError
          launchContext={launchContext}
          message={authState.message}
        />
      );
    }

    return (
      <Navigate
        to={{ pathname: "/", search: location.search }}
        replace
        state={{ authError: authState.message }}
      />
    );
  }

  return element;
}

function TeacherPortalRedirect() {
  const location = useLocation();

  return (
    <Navigate
      to={{ pathname: "/masterdashboard", search: location.search }}
      replace
    />
  );
}

function ClassroomTransitionRedirect() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  searchParams.set("workflow", "classroom-transition");

  return (
    <Navigate
      to={{ pathname: "/masterdashboard", search: `?${searchParams.toString()}` }}
      replace
    />
  );
}

export function PortalRoutes() {
  return (
    <Suspense fallback={loadingFallback}>
      <Routes>
        <Route path="/" element={<DevelopmentEntry />} />
        <Route
          path="/masterdashboard"
          element={<TeacherProtectedRoute element={<MasterDashboard />} />}
        />
        <Route
          path="/classroom-transition"
          element={
            <TeacherProtectedRoute
              element={<ClassroomTransitionRedirect />}
            />
          }
        />
        <Route
          path="/profile"
          element={<TeacherProtectedRoute element={<TeacherProfile />} />}
        />
        <Route
          path="*"
          element={
            <TeacherProtectedRoute
              element={<TeacherPortalRedirect />}
            />
          }
        />
      </Routes>
    </Suspense>
  );
}

export function RootComponent() {
  return (
    <BrowserRouter>
      <PortalRoutes />
    </BrowserRouter>
  );
}

const container = document.getElementById("root");

if (container) {
  const root = createRoot(container);
  root.render(<RootComponent />);
}
